import Highlight from "@/components/home/Highlight";
import { Head, Link, usePage } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";
import Parallax from "@/components/home/Parallax";
import { OpenBookDoodle, ClosedBookDoodle, PencilDoodle, SmallStackDoodle, SectionDivider } from "@/components/home/illustrations";
import foundersPhoto from "@/images/rebecca-and-jordan.jpg";

export default function AboutUs() {
  const { routes } = usePage().props;

  return (
    <MarketingLayout>
      <Head title="About us | Rebecca & Jordan" />
      <PageContainer width="wide" className="pt-10 md:pt-14 pb-10 md:pb-14">
        <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr] lg:gap-14 items-center">
          <div className="space-y-5">
            <Badge className="uppercase tracking-wider">Our story · Edmonton, Alberta</Badge>
            <h1 className="text-4xl md:text-6xl leading-tight">Built for our school.<br />Shared with <Highlight color="forest">yours.</Highlight></h1>
            <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
              We’re Rebecca and Jordan, a wife and husband in Edmonton.
              We built PagePledge for our kids’ elementary school, then wanted to share it
              with other school communities.
            </p>
            <p className="max-w-2xl leading-relaxed text-muted-foreground">
              Our aim is simple: help schools keep more of what they raise,
              get students excited about reading, and give volunteers and
              staff more of their time back.
            </p>
          </div>
          <figure className="w-full max-w-md mx-auto">
            <img
              src={foundersPhoto}
              alt="Rebecca and Jordan, the wife-and-husband team behind PagePledge"
              width="1067"
              height="1600"
              fetchPriority="high"
              className="aspect-[4/5] w-full object-cover object-[center_45%] rounded-2xl border border-border/60 shadow-sm"
            />
            <figcaption className="mt-3 text-sm text-center text-muted-foreground">
              Rebecca & Jordan · Co-founders
            </figcaption>
          </figure>
        </div>
      </PageContainer>

      <PageContainer width="narrow" className="py-10 md:py-12" outerClassName="relative overflow-hidden bg-accent/5 border-y border-accent/15">
        <div aria-hidden="true" className="pointer-events-none hidden lg:block">
          <Parallax speed={0.07} className="absolute top-44 left-[calc(50%-31rem)] opacity-80">
            <OpenBookDoodle size={88} rotate={-12} />
          </Parallax>
          <Parallax speed={0.1} className="absolute top-[45%] right-[calc(50%-31rem)] opacity-80">
            <PencilDoodle size={84} rotate={-22} />
          </Parallax>
          <Parallax speed={0.05} className="absolute bottom-12 left-[calc(50%-30rem)] opacity-80">
            <ClosedBookDoodle size={66} rotate={12} color="amber" />
          </Parallax>
        </div>
        <article className="relative space-y-8">
          <section aria-labelledby="why-we-built-it" className="space-y-5">
            <h2 id="why-we-built-it" className="text-3xl md:text-4xl leading-tight">Why we built PagePledge</h2>
            <div className="space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>
                After years of using other fundraising platforms at our school,
                we kept coming back to the same frustrations. We’d seen platform
                fees take 20% of what a school raised. When families and friends
                donate to support students, that’s a lot of money to lose before
                the school can put it to use.
              </p>
              <p>
                Running the fundraiser was hard work, too. The administration
                took time from volunteers and school staff who already had
                plenty to do. We loved what a read-a-thon could do for our
                school: get children excited about books, encourage a reading
                habit, and raise money for the things they need. We wanted
                organising one to be easier and less expensive.
              </p>
              <p>
                Rebecca is head of the parent council at an Edmonton Public
                school, and Jordan is a software developer. Between us, we had
                experience with the work involved and the skills to build
                something that could help. So we decided to make an app for
                our kids’ elementary school.
              </p>
            </div>
          </section>
          <SectionDivider variant="book" className="max-w-48 mx-auto" />
          <section aria-labelledby="sharing-pagepledge" className="space-y-5">
            <h2 id="sharing-pagepledge" className="text-3xl md:text-4xl leading-tight">Making it available to other schools</h2>
            <div className="space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>
                That project became PagePledge. We built it to take some of the
                administrative work out of a read-a-thon and help our school
                keep more of the money it raised. We chose a flat campaign fee
                so schools know the cost upfront, and raising more doesn’t
                mean paying us more.
              </p>
              <p>
                Now we want to offer it to other school communities facing
                the same challenges. Our hope is that a simpler, more
                affordable fundraiser leaves volunteers and staff with more
                time, schools with more funding, and students with more
                encouragement to keep reading.
              </p>
            </div>
          </section>
        </article>
      </PageContainer>

      <PageContainer width="narrow" className="text-center space-y-6 pb-12 md:pb-16" outerClassName="relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none hidden lg:block">
          <Parallax speed={0.04} className="absolute bottom-8 right-[calc(50%-31rem)]">
            <SmallStackDoodle size={92} rotate={-6} />
          </Parallax>
        </div>
        <SectionHeading flourish="from our school to yours" title="Let’s help your school get reading."
          subhead="Early adopter discounts are available, with hands-on onboarding. Start with a free trial; running a campaign costs a flat fee." />
        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" asChild><Link href={routes.new_school_application}>Sign up your school <ArrowRight className="h-4 w-4" /></Link></Button>
          <Button size="lg" variant="outline" asChild><Link href={routes.contact}>Get in touch</Link></Button>
        </div>
      </PageContainer>
    </MarketingLayout>
  );
}
